import React, { useState } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { Eye, EyeOff, Lock } from 'lucide-react';

const SetPasswordModal = ({ employee, authHeader, onClose, onPasswordSet }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate passwords
    if (!password || password.length < 6) {
      toast({
        title: "Помилка",
        description: "Пароль повинен містити мінімум 6 символів",
        variant: "destructive",
      });
      return;
    }
    
    if (password !== confirmPassword) {
      toast({
        title: "Помилка",
        description: "Паролі не співпадають",
        variant: "destructive",
      });
      return;
    }
    
    setLoading(true);
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      const apiUrl = BACKEND_URL ? `${BACKEND_URL}/api/employees/${employee.id}/set-password` : `/api/employees/${employee.id}/set-password`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader
        },
        body: JSON.stringify({ password })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast({
          title: "Успіх",
          description: `Пароль для ${employee.name} успішно встановлено`,
          variant: "default",
        });
        
        // Clear form
        setPassword('');
        setConfirmPassword('');
        
        // Notify parent component
        if (onPasswordSet) {
          onPasswordSet(employee.id);
        }
        
        // Close modal
        if (onClose) {
          onClose();
        }
      } else {
        toast({
          title: "Помилка",
          description: data.error || "Не вдалося встановити пароль",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error setting password:', error);
      toast({
        title: "Помилка",
        description: "Не вдалося встановити пароль",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!employee} onOpenChange={(open) => {
      if (!open && onClose) {
        onClose();
      }
    }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Lock className="w-5 h-5" />
            <span>Встановити пароль</span>
          </DialogTitle>
        </DialogHeader>
        
        {employee && (
          <div className="py-2">
            <p className="text-sm text-gray-600 mb-4">
              Встановлення пароля для: <strong>{employee.name}</strong>
            </p>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium text-gray-700">
                  Новий пароль
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pr-10"
                    placeholder="Введіть новий пароль"
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
                  Підтвердіть пароль
                </label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pr-10"
                    placeholder="Підтвердіть новий пароль"
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <DialogFooter className="flex space-x-2">
                <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
                  Скасувати
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Встановлення...' : 'Встановити пароль'}
                </Button>
              </DialogFooter>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SetPasswordModal;